import {
  makeCbUnicoStartPage,
  makeContentEnrollmentPage,
  makeProfileAssessmentCallbackPage,
  makeProfileAssessmentGetStartedPage,
  makeProfileAssessmentInProgressPage
} from '@/main/factories/pages'
import { makeContentCompletePage } from '@/main/factories/pages/content-complete-factory'
import { makeContentUnsubscribePage } from '@/main/factories/pages/content-unsubscribe-factory'
import {
  makeDependentContentPage,
  makeDependentRequestCatalogPage,
  makeDependentRequestPage
} from '@/main/factories/pages/dependent-request-factory'
import {
  makeRefundProgramListPage,
  makeRefundRequestPage
} from '@/main/factories/pages/refund-factory'
import { makeUpgradeRequestPage } from '@/main/factories/pages/upgrade-request-factory'
import BuildCompanyManagerRoutes from '@/main/routes/builders/company-manager-routes-builder'
import BuildUserDataRoutes from '@/main/routes/builders/user-data-routes-builder'
import ContentUnsubscribeLoader from '@/main/routes/loaders/content/content-unsubscribe-loader'
import DependentContentLoader from '@/main/routes/loaders/dependent-request/dependent-content-loader'
import RefundProgramListLoader from '@/main/routes/loaders/refund/refund-program-list-loader'
import { ValidAccountState } from '@/presentation/contexts/auth/account-provider'
import useAccount from '@/presentation/hooks/auth/use-account'
import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk'
import { useQueryClient } from 'react-query'
import { RouteObject } from 'react-router-dom'
import ContentCompletionLoader from '../loaders/content/content-completion-loader'
import ContentEnrollmentLoader from '../loaders/content/content-enrollment-loader'
import DependentRequestLoader from '../loaders/dependent-request/dependent-request-loader'
import RefundRequestLoader from '../loaders/refund/refund-request-loader'
import DependentCatalogLoader from '../loaders/search/dependent-catalog-loader'
import UpgradeRequestLoader from '../loaders/upgrade-request/upgrade-request-loader'
import PathConstants from '../path-contants'
import BuildContentDiscoveryRoutes from './content-discovery-routes-builder'
import BuildInternalCompanyManagerRoutes from './internal-company-manager-routes-builder'
import BuildOnboardingRoutes from './onboarding-routes-builder'
import { makeContactVerificationPage } from '@/main/factories/pages/contact-verification-factory'
import { contactVerificationLoader } from '@/main/routes/loaders/contact_verification/contact-verification-loader'
import LoggedLayout from '@/presentation/components/ui/layout/logged-layout'
import BasicLayout from '@/presentation/components/ui/layout/basic-layout'

const {
  CONTENT_ENROLLMENT,
  CONTENT_UNSUBSCRIBE,
  CONTENT_COMPLETE,
  CBU_START,
  PROFILE_ASSESSMENT_CALLBACK,
  PROFILE_ASSESSMENT_IN_PROGRESS,
  PROFILE_ASSESSMENT_GET_STARTED,
  REFUND,
  REFUND_REQUEST,
  UPGRADE_REQUEST,
  DEPENDENT_REQUEST,
  DEPENDENT_REQUEST_CATALOG,
  DEPENDENT_REQUEST_CONTENT,
  HOME,
  CONTACT_VERIFICATION
} = PathConstants.Routes

const AuthenticatedRoutesBuilder = (): RouteObject[] => {
  const [account] = useAccount() as ValidAccountState
  const queryClient = useQueryClient()
  const ldClient = useLDClient() as LDClient
  return [
    ...BuildOnboardingRoutes(),
    ...BuildCompanyManagerRoutes(),
    ...BuildInternalCompanyManagerRoutes(),
    ...BuildUserDataRoutes(),
    {
      element: <LoggedLayout navMenu="main" footerVariant="full" enableSearch={true} />,
      children: [
        ...BuildContentDiscoveryRoutes(),
        {
          path: REFUND.value,
          element: makeRefundProgramListPage(),
          loader: new RefundProgramListLoader(account, queryClient, ldClient).loader
        }
      ]
    },
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          path: CONTENT_ENROLLMENT.value,
          element: makeContentEnrollmentPage(),
          loader: new ContentEnrollmentLoader(account, queryClient, ldClient).loader
        },
        {
          path: CONTENT_UNSUBSCRIBE.value,
          element: makeContentUnsubscribePage(),
          loader: new ContentUnsubscribeLoader(account, queryClient, ldClient).loader
        },
        {
          path: CONTENT_COMPLETE.value,
          element: makeContentCompletePage(),
          loader: new ContentCompletionLoader(account, HOME.value, queryClient, ldClient).loader
        },
        {
          path: CBU_START.value,
          element: makeCbUnicoStartPage()
        },
        {
          path: PROFILE_ASSESSMENT_CALLBACK.value,
          element: makeProfileAssessmentCallbackPage()
        },
        {
          path: PROFILE_ASSESSMENT_IN_PROGRESS.value,
          element: makeProfileAssessmentInProgressPage()
        },
        {
          path: PROFILE_ASSESSMENT_GET_STARTED.value,
          element: makeProfileAssessmentGetStartedPage()
        },
        {
          path: REFUND_REQUEST.value,
          element: makeRefundRequestPage(),
          loader: new RefundRequestLoader(account, queryClient, ldClient).loader
        },
        {
          path: UPGRADE_REQUEST.value,
          element: makeUpgradeRequestPage(),
          loader: new UpgradeRequestLoader(account, queryClient, ldClient).loader
        },
        {
          path: DEPENDENT_REQUEST.value,
          element: makeDependentRequestPage(),
          loader: new DependentRequestLoader(account, queryClient, ldClient).loader
        },
        {
          path: DEPENDENT_REQUEST_CATALOG.value,
          element: makeDependentRequestCatalogPage(),
          loader: new DependentCatalogLoader(account, queryClient, ldClient).loader
        },
        {
          path: DEPENDENT_REQUEST_CONTENT.value,
          id: DEPENDENT_REQUEST_CONTENT.name,
          element: makeDependentContentPage(),
          loader: new DependentContentLoader(account, queryClient, ldClient).loader
        },
        {
          path: CONTACT_VERIFICATION.value,
          element: makeContactVerificationPage(),
          loader: contactVerificationLoader
        }
      ]
    }
  ]
}
export default AuthenticatedRoutesBuilder
